import { useHistory, useRouteMatch } from "react-router";

import { routes } from "utilities/routes";

import { BTLayout } from "commonComponents/btWrappers/BTLayout/BTLayout";
import {
    BTListLayout,
    BTListLayoutWhitespace,
} from "commonComponents/btWrappers/BTListLayout/BTListLayout";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { EmptyState } from "commonComponents/entity/emptyState/EmptyState";
import { JobsitesRequiredPresentational } from "commonComponents/helpers/JobsitesRequired/JobsitesRequiredPresentational/JobsitesRequiredPresentational";
import { JobPickerFilterTypes } from "commonComponents/utilities/JobPicker/JobPicker.api.types";

import { type SelectedFilterItem } from "entity/filters/Filter/Filter.api.types";

import FilterSVG from "images/Filter.svg?react";

export interface IJobsitesRequired {
    jobIds?: number[];
    isTemplateMode?: boolean;
    jobPickerReady?: boolean;
    requireSingleJob?: boolean;
    allowTemplates?: boolean;
    setSelectedJobId?: (jobId: number, orgId?: number, loadMissingJobs?: boolean) => Promise<void>;
    selectedFilters?: SelectedFilterItem[];
    requireSingleJobGroup?: boolean;
    isAllJobsSelected?: boolean;
}

/**
 * an HoC that requires a valid set of jobs selected before it will render its wrapped component
 */
export const withJobsitesRequired =
    (
        listTitle?: string,
        StandaloneDetailRouteComponent?: React.ComponentType<{ modalConfig?: IModalConfiguration }>,
    ) =>
    <P extends object>(
        Component: React.ComponentType<P & IJobsitesRequired>,
    ): React.FunctionComponent<P & IJobsitesRequired> => {
        return function WithJobsRequired(props) {
            const match = useRouteMatch();
            const history = useHistory();
            const {
                jobIds,
                isTemplateMode,
                jobPickerReady,
                requireSingleJob,
                allowTemplates = true,
                requireSingleJobGroup,
                selectedFilters,
                isAllJobsSelected,
            } = props;

            if (!allowTemplates && isTemplateMode) {
                history.replace(routes.template.getListLink());
                return null;
            }

            const isInvalidJobSelection =
                jobPickerReady &&
                (!jobIds || jobIds.length === 0 || (requireSingleJob && jobIds.length !== 1));

            if (requireSingleJobGroup) {
                const selectedJobGroups = selectedFilters?.find(
                    (filter) => filter.key === JobPickerFilterTypes.Neighborhood,
                )?.selectedValue as number[] | undefined;
                const selectedJobGroupFilterLength = selectedJobGroups?.length ?? 0;

                const containsUnassignedJobGroup = selectedJobGroups?.includes(-1);
                if (
                    isAllJobsSelected &&
                    (selectedJobGroupFilterLength !== 1 || containsUnassignedJobGroup)
                ) {
                    const innerComponent = (
                        <BTListLayoutWhitespace>
                            <EmptyState
                                title="No Job Group selected"
                                image={<FilterSVG width={72} height={72} />}
                                className="JobsitesRequiredPresentationalContainer"
                            >
                                Filter your jobs list to a single Job Group to use this view
                            </EmptyState>
                        </BTListLayoutWhitespace>
                    );

                    return listTitle ? (
                        <BTListLayout title={listTitle}>{innerComponent}</BTListLayout>
                    ) : (
                        <BTLayout>{innerComponent}</BTLayout>
                    );
                }
            }

            if (isInvalidJobSelection) {
                return (
                    <>
                        <JobsitesRequiredPresentational
                            listTitle={listTitle}
                            isTemplateMode={isTemplateMode}
                        />
                        {StandaloneDetailRouteComponent && (
                            <StandaloneDetailRouteComponent
                                modalConfig={{
                                    beforeClose: () => history.push(match.url),
                                    parentRoute: match.url,
                                }}
                            />
                        )}
                    </>
                );
            }

            return <Component {...props} />;
        };
    };
